"use client";
import React, { useRef, useState, useEffect } from "react";
import Image from "next/image";
import { HeroSectionProps } from "./types";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";

const HeroSection = ({
  backgroundImage,
  tabs,
  heading,
  description,
  ctaLabel,
  ctaHref,
  customBadge,
  onCtaClick,
  marqueeText,
  renderCards,
}: HeroSectionProps): React.JSX.Element => {
  const [active, setActive] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const updateSlider = (index: number) => {
    if (!tabs || tabs.length === 0) return;
    const btn = btnRefs.current[index];
    const wrapper = wrapperRef.current;
    const slider = sliderRef.current;
    if (!btn || !wrapper || !slider) return;
    const wRect = wrapper.getBoundingClientRect();
    const bRect = btn.getBoundingClientRect();
    slider.style.width = `${bRect.width}px`;
    slider.style.transform = `translateX(${bRect.left - wRect.left - 3}px)`;
  };

  useEffect(() => {
    if (tabs && tabs.length > 0) {
      if (sliderRef.current) sliderRef.current.style.transition = "none";
      updateSlider(0);
      setTimeout(() => {
        if (sliderRef.current) sliderRef.current.style.transition = "";
      }, 50);
    }
  }, [tabs]);

  useEffect(() => {
    if (tabs && tabs.length > 0) {
      updateSlider(active);
    }
  }, [active, tabs]);

  useEffect(() => {
    const handleResize = () => {
      if (tabs && tabs.length > 0) updateSlider(active);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [active, tabs]);

  const bgSrc =
    typeof backgroundImage === "string" ? backgroundImage : backgroundImage.src;
  const bgW =
    typeof backgroundImage === "string" ? 1600 : backgroundImage.width;
  const bgH =
    typeof backgroundImage === "string" ? 900 : backgroundImage.height;

  const resolvedCtaHref =
    ctaHref ?? `/contact-us?interest=${ctaLabel.slice(4)}`;

  return (
    <>
      <div className="w-full max-w-[1600px] mx-auto overflow-x-hidden">
        <div className="relative w-full mt-24">
          <Image
            src={bgSrc}
            alt="Background"
            className="w-full block"
            width={bgW}
            height={bgH}
            priority
          />

          <div className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 sm:gap-5 px-4 pb-6 sm:pb-28 lg:pb-48">
            {customBadge ? (
              customBadge
            ) : tabs && tabs.length > 0 ? (
              <div
                ref={wrapperRef}
                className="relative h-[44px] sm:h-[55px] w-fit inline-flex items-center gap-0.5 rounded-full bg-[#E7E7EE] backdrop-blur-sm border border-white/30 p-[3px]"
              >
                <div
                  ref={sliderRef}
                  className="absolute top-[3px] left-[3px] h-[calc(100%-6px)] rounded-full transition-[transform,width] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)]"
                />
                {tabs.map((tab, i) => (
                  <button
                    key={i}
                    ref={(el) => {
                      btnRefs.current[i] = el;
                    }}
                    onClick={() => {
                      setActive(i);
                      updateSlider(i);
                    }}
                    className={`relative z-10 rounded-full h-full px-3 sm:px-5 py-1.5 text-[11px] sm:text-sm whitespace-nowrap transition-colors ${active === i
                        ? "text-white/90 bg-[linear-gradient(to_right,_#056686,_#8ABA28)] font-medium"
                        : "text-[#204969]"
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : null}

            <h1 className="font-semibold text-[clamp(24px,5.2vw,86px)] text-center leading-tight px-2">
              <span className="text-[#373A74] block">{heading.line1}</span>
              {heading.plainLine2 ? (
                <span className="text-[#373A74] block">{heading.line2}</span>
              ) : (
                <span className="bg-[linear-gradient(252.81deg,_#8ABA28_-24.42%,_#056686_105.73%)] bg-clip-text text-transparent block">
                  {heading.line2}
                </span>
              )}
              {heading.line3 && (
                <span className="text-[#373A74] block">{heading.line3}</span>
              )}
            </h1>

            <p className="text-[clamp(13px,1.5vw,22px)] text-[#373A74]/80 text-center leading-snug max-w-4xl px-4 font-normal">
              {description}
            </p>

            <Link href={resolvedCtaHref}>
              <button
                onClick={onCtaClick}
                className="inline-flex items-center justify-center hover:cursor-pointer bg-[linear-gradient(to_right,_#056686,_#8ABA28)] text-white px-6 py-2.5 sm:px-7 sm:py-3 rounded-full text-[13px] sm:text-[14px] font-medium whitespace-nowrap transition-all duration-300 hover:opacity-90 active:scale-[0.98] gap-2 shadow-xs"
              >
                {ctaLabel}
                <ArrowUpRight size={17} />
              </button>
            </Link>
          </div>

          {marqueeText && (
            <div className="hidden sm:block absolute bottom-[40px] sm:bottom-[70px] w-full overflow-hidden pointer-events-none z-10">
              <div className="whitespace-nowrap text-[clamp(50px,12vw,220px)] font-semibold text-transparent stroke-text animate-marquee">
                {marqueeText}
              </div>
            </div>
          )}
        </div>

        {renderCards}
      </div>
    </>
  );
};

export default HeroSection;
